// Development fixture for the Vent tab (`?mock=vent`): the ComfoAir state and the vent_* export files on the
// same winter day as the Temp mock. Preheat is on with the intake plateaued at −2 °C, recovery ≈ 86 %, the
// level follows the plan (low 09–12 and 16–20, high 01–05, else middle), filter 2140 h, last 30 days
// 192 / 418 / 110 h. The curves are the prototypes' (docs/temp-vent-tabs-redesign/prototype-vent-*.html).
import { VentLevel, VentilationState } from '../../common';
import { RrdFile } from './rrd';
import { localMidnight } from './tallinn_time';
import { Curve, hod, mockRrd, outdoor, rangeOf, tuba } from './temp_mock';

const H = 3_600_000;

const LEVELS: VentLevel[] = ['away', 'low', 'middle', 'high'];
const PERCENTS = [15, 30, 45, 70]; // supply = exhaust per level

// 1 = low, 2 = middle, 3 = high (as energy_mock's plan).
const level = (t: number) => {
  const h = hod(t);
  return (h >= 9 && h < 12) || (h >= 16 && h < 20) ? 1 : h >= 1 && h < 5 ? 3 : 2;
};
const preheat = (t: number) => outdoor(t) < -2;
const bypass = (t: number) => (outdoor(t) > 15 && tuba(t) > 22 ? 100 : 0);

const intake = (t: number) => (outdoor(t) < -2 ? -2 + 0.15 * Math.sin(t * 3) : outdoor(t));
const extract = (t: number) => tuba(t) + 0.4;
const supply = (t: number) => {
  const i = intake(t);
  return bypass(t) ? i + 1.2 : i + 0.86 * (extract(t) - i) - 0.15 * (level(t) - 2);
};
const exhaust = (t: number) => (bypass(t) ? extract(t) - 0.4 : extract(t) - (supply(t) - intake(t)) + 0.3);

// Lifetime counters (getOperatingHours) and their growth over the last 30 days.
const HOURS = {
  away: 12,
  low: 8420,
  middle: 11260,
  high: 2180,
  frostProtection: 46,
  preHeating: 1320,
  bypassOpen: 3410,
  filter: 2140,
};
const HOURS_30D: Record<string, number> = {
  hAway: 0,
  hLow: 192,
  hMiddle: 418,
  hHigh: 110,
  hFrost: 2,
  hPreheat: 96,
  hBypass: 0,
};
const HOURS_NOW: Record<string, number> = {
  hAway: HOURS.away,
  hLow: HOURS.low,
  hMiddle: HOURS.middle,
  hHigh: HOURS.high,
  hFrost: HOURS.frostProtection,
  hPreheat: HOURS.preHeating,
  hBypass: HOURS.bypassOpen,
};

// vent_temperature<range>, vent_level<range>, vent_hours30d. `legacy`: only the temperatures (older exports).
export function ventMockFile(name: string, now: number, legacy: boolean): RrdFile | undefined {
  if (name === 'vent_hours30d') {
    if (legacy) {
      return undefined;
    }
    // 12-hour LAST rows over 31 days; the first and last rows are 30 days apart.
    const step = 12 * H;
    const end = Math.floor(now / step) * step;
    const first = end - 30 * 24 * H;
    const legends = Object.keys(HOURS_30D);
    const data: (number | null)[][] = [[(first - step) / 1000, ...legends.map(() => null)]];
    for (let ts = first; ts <= end; ts += step) {
      const f = (ts - first) / (end - first);
      data.push([ts / 1000, ...legends.map((k) => Math.round(HOURS_NOW[k] - HOURS_30D[k] * (1 - f)))]);
    }
    return { meta: { legend: legends, step: 43200 }, data };
  }
  const range = rangeOf(name);
  if (!range) {
    return undefined;
  }
  if (name.startsWith('vent_temperature')) {
    const curves: Curve[] = [intake, supply, extract, exhaust];
    return mockRrd(['Välisõhk', 'Sissepuhe', 'Toast tulev', 'Väljapuhe'], curves, range, now, { lastNull: true });
  }
  if (name.startsWith('vent_level') && !legacy) {
    return mockRrd(['Tase'], [(t) => level(t) + 1], range, now);
  }
  return undefined;
}

// What vent.js posts (GET /ventilation/state). `legacy`: an older vent.js / server (no optional fields).
export function ventMockState(now: number, legacy: boolean): VentilationState {
  const t = (now - localMidnight(now) - 12_000) / H;
  const lv = level(t);
  const pct = PERCENTS[lv];
  const r1 = (v: number) => Math.round(v * 10) / 10;
  const state: VentilationState = {
    temperatures: {
      outsideAir: r1(intake(t)),
      supplyAir: r1(supply(t)),
      outgoingAir: r1(extract(t)),
      exhaustAir: r1(exhaust(t)),
      comfortValue: 20,
    },
    airFlows: {
      rotationsSupply: Math.round((pct / 45) * 1320),
      rotationsOutgoing: Math.round((pct / 45) * 1290),
      supplyAirPercentage: pct,
      outgoingAirPercentage: pct,
    },
    bypassPercentage: bypass(t),
    preheat: preheat(t),
    filterHours: HOURS.filter,
    supplyFanRunning: true,
    currentLevel: LEVELS[lv],
  };
  if (legacy) {
    return state;
  }
  return {
    ...state,
    operatingHours: HOURS,
    levelPercents: {
      away: { supply: 15, exhaust: 15 },
      low: { supply: 30, exhaust: 30 },
      middle: { supply: 45, exhaust: 45 },
      high: { supply: 70, exhaust: 70 },
    },
    control: { lastCommandedLevel: LEVELS[lv], lastCommandedAt: now - 20 * 60_000 },
    receivedAt: now - 12_000,
  };
}
